import React, { useEffect, useState } from "react";
import axios from "axios";
import AlvnaBrand from "../components/AlvnaBrand";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, buttonPrimary, inputBase } from "../theme";

const REQUESTABLE_FIELDS = [
  { key: "requested_max_categories", defaultKey: "max_categories", label: "Menu categories" },
  { key: "requested_max_items_per_category", defaultKey: "max_items_per_category", label: "Items per category" },
  { key: "requested_max_tables", defaultKey: "max_tables", label: "Tables" },
  { key: "requested_max_staff", defaultKey: "max_staff", label: "Staff accounts" },
  { key: "requested_max_employees", defaultKey: "max_employees", label: "Employees (Employee module)" },
  { key: "requested_report_retention_days", defaultKey: "report_retention_days", label: "Report history (days)" },
];

export default function Register() {
  const [form, setForm] = useState({
    business_name: "", owner_name: "", phone_number: "", email: "",
    restaurant_type: "", city: "", notes: "",
  });
  const [requests, setRequests] = useState({});
  const [requestSplitBill, setRequestSplitBill] = useState(false);
  const [requestEmployeeModule, setRequestEmployeeModule] = useState(false);
  const [requestOnline, setRequestOnline] = useState(false);
  const [requestCards, setRequestCards] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [defaults, setDefaults] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);

  useEffect(() => {
    axios.get(endpoints.defaultCaps).then((res) => setDefaults(res.data)).catch(() => {});
  }, []);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const openTerms = (e) => {
    e.preventDefault();
    setError("");
    setShowTerms(true);
  };

  const confirmAndSubmit = async () => {
    setTermsAgreed(true);
    try {
      await axios.post(endpoints.register, {
        ...form,
        ...requests,
        requested_split_bill_enabled: requestSplitBill,
        requested_attendance_enabled: requestEmployeeModule,
        requested_online_ordering_enabled: requestOnline,
        requested_online_pay_stripe_allowed: requestOnline && requestCards,
        terms_agreed: true,
      });
      setShowTerms(false);
      setSubmitted(true);
    } catch (err) {
      setShowTerms(false);
      setError(err.response?.data?.error || "Something went wrong.");
    }
  };

  if (submitted) {
    return (
      <div style={styles.page}>
        <div className="reg-card" style={styles.card}>
          <style>{REG_CSS}</style>
          <AlvnaBrand variant="full" />
          <div style={{ padding: space[5], textAlign: "center" }}>
            <h2 style={styles.heading}>Thanks for registering!</h2>
            <p style={{ fontFamily: fonts.body, color: colors.textSecondary }}>
              Your registration is pending approval. We'll email you once it's approved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div className="reg-card" style={styles.card}>
        <style>{REG_CSS}</style>
        <div className="reg-left">
          <AlvnaBrand variant="full" />
          <p className="reg-pitch">Everything a restaurant needs to run its floor and its kitchen, set up in minutes and free to start.</p>
          {defaults && (
            <div style={styles.includedBox}>
              <div style={styles.includedTitle}>What's Included</div>
              <ul style={styles.includedList}>
                <li>{defaults.max_categories} menu categories</li>
                <li>{defaults.max_items_per_category} items per category</li>
                <li>{defaults.max_tables} tables</li>
                <li>{defaults.max_staff} staff accounts (waiter, kitchen, manager)</li>
                <li>{defaults.report_retention_days}-day report history</li>
                {defaults.split_bill_enabled && <li>Split Bill included</li>}
                {defaults.attendance_enabled
                  ? <li>Employee module included (clock in/out with GPS and photo, up to {defaults.max_employees} employees)</li>
                  : <li>Employee module (clock in/out with GPS and photo, hours export): optional add-on, request it below</li>}
                {defaults.online_ordering_enabled
                  ? <li>Online ordering for pickup, with order tracking, included</li>
                  : <li>Online ordering for pickup (ASAP or scheduled) with order tracking: optional add-on, request it below</li>}
                {defaults.online_pay_stripe_allowed
                  ? <li>Online card payments (Stripe) included</li>
                  : <li>Online card payments (Stripe): optional add-on for online ordering</li>}
              </ul>
            </div>
          )}
        </div>

        <form onSubmit={openTerms} style={styles.form} className="reg-right">
          <h2 style={styles.heading}>Get Started Free</h2>
          {error && <div style={styles.error}>{error}</div>}
          <input style={styles.input} placeholder="Business name" value={form.business_name} onChange={update("business_name")} required />
          <input style={styles.input} placeholder="Owner name" value={form.owner_name} onChange={update("owner_name")} required />
          <input style={styles.input} placeholder="Phone number" value={form.phone_number} onChange={update("phone_number")} required />
          <input style={styles.input} type="email" placeholder="Email" value={form.email} onChange={update("email")} required />
          <input style={styles.input} placeholder="Restaurant type (e.g. cafe, fine dining)" value={form.restaurant_type} onChange={update("restaurant_type")} />
          <input style={styles.input} placeholder="City" value={form.city} onChange={update("city")} />
          <textarea style={{ ...styles.input, height: 80 }} placeholder="Anything else we should know?" value={form.notes} onChange={update("notes")} />

          {defaults && (
            <div style={styles.requestBox}>
              <div style={styles.requestTitle}>
                Want more? Enter the number you'd like below. Leave blank to use the standard limit.
              </div>
              {REQUESTABLE_FIELDS.map(({ key, defaultKey, label }) => (
                <div key={key} style={styles.requestRow}>
                  <span style={styles.requestLabel}>{label} <span style={styles.requestDefault}>(standard: {defaults[defaultKey]})</span></span>
                  <input
                    style={styles.requestInput}
                    type="number"
                    min="0"
                    placeholder={String(defaults[defaultKey])}
                    value={requests[key] || ""}
                    onChange={(e) => setRequests({ ...requests, [key]: e.target.value })}
                  />
                </div>
              ))}
              {!defaults.split_bill_enabled && (
                <label style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={requestSplitBill}
                    onChange={(e) => setRequestSplitBill(e.target.checked)}
                  />
                  Request Split Bill
                </label>
              )}
              {!defaults.attendance_enabled && (
                <label style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={requestEmployeeModule}
                    onChange={(e) => setRequestEmployeeModule(e.target.checked)}
                  />
                  Request the Employee module (enter how many employees above)
                </label>
              )}
              {!defaults.online_ordering_enabled && (
                <label style={styles.checkboxRow}>
                  <input type="checkbox" checked={requestOnline} onChange={(e) => { setRequestOnline(e.target.checked); if (!e.target.checked) setRequestCards(false); }} />
                  Request Online Ordering (pickup orders with tracking)
                </label>
              )}
              {!defaults.online_pay_stripe_allowed && (
                <label style={{ ...styles.checkboxRow, opacity: requestOnline ? 1 : 0.5 }}>
                  <input type="checkbox" checked={requestCards} disabled={!requestOnline} onChange={(e) => setRequestCards(e.target.checked)} />
                  Also request online card payments (Stripe)
                </label>
              )}
            </div>
          )}

          <button style={styles.button} type="submit">Submit Registration</button>
        </form>
      </div>
      {/* .reg-card above is a CSS grid on desktop: reg-left (brand + benefits) beside reg-right (the form), so the page uses the full width instead of one narrow centered column. */}

      {showTerms && (
        <TermsModal onAgree={confirmAndSubmit} onClose={() => setShowTerms(false)} />
      )}
    </div>
  );
}

function TermsModal({ onAgree, onClose }) {
  const [checked, setChecked] = useState(false);
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.termsModal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontFamily: fonts.display, margin: "0 0 12px" }}>Privacy &amp; Terms</h3>
        <div style={styles.termsBody}>
          <p><strong>What we collect:</strong> your business name, owner name, phone, email, and the menu/table/order data you enter, used solely to operate Alvna Remo for your account.</p>
          <p><strong>Contact:</strong> Alvna may contact you about your account, for support, and specifically if a limit-increase request you submit may involve a subscription fee, we'll always confirm with you directly before anything is charged.</p>
          <p><strong>Alvna's rights:</strong> we may approve, suspend, or terminate accounts, and may modify or discontinue the service at any time.</p>
          <p><strong>Your responsibilities:</strong> keep your account info accurate and use the platform appropriately.</p>
          <p><strong>Service availability:</strong> Alvna Remo is provided "as is," with no guarantee of uninterrupted or error-free service. Downtime can occur for maintenance, technical issues, or events outside our control. We aren't liable for losses from downtime or interruptions, and our total liability is limited to fees you've actually paid us. Since this is a trial platform, please keep your own independent records rather than relying on it as your only copy of your data.</p>
          <p><strong>Employee module (if you use it):</strong> when your team clocks in or out, their phone's location and a photo taken at that moment are recorded, together with the time. They are visible only to the restaurant's owner and managers. Photos and hours records stay visible for a limited period set for your restaurant (30 and 90 days unless changed); after that they are moved to an archive held by Alvna, which is deleted only after a further period, if any. As the restaurant, you are responsible for letting your employees know and for following the workplace-monitoring and privacy laws that apply to you.</p>
          <p><strong>Online ordering and payments (if you use them):</strong> your customers' name, phone number, optional email and order details are stored to take and track their orders, and erased from finished orders after a limited period. Card payments are processed by Stripe on the restaurant's own Stripe account and are subject to Stripe's terms, including its Connected Account Agreement; the restaurant, not Alvna, is the seller and is responsible for its menu, prices, taxes, refunds and customer service. Alvna does not hold or store card details.</p>
          <p><strong>Questions?</strong> contact@alvna.com</p>
        </div>
        <label style={styles.termsCheckboxRow}>
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
          I have read and agree to the Privacy &amp; Terms above.
        </label>
        <div style={{ display: "flex", gap: space[2], marginTop: space[3] }}>
          <button style={{ ...buttonPrimary, opacity: checked ? 1 : 0.5, flex: 1 }} disabled={!checked} onClick={onAgree}>
            I Agree and Submit Registration
          </button>
        </div>
      </div>
    </div>
  );
}

const REG_CSS = `
@media (min-width: 900px) {
  .reg-card{max-width:1040px!important;display:grid;grid-template-columns:380px minmax(0,1fr);align-items:stretch}
  .reg-left{background:#f3f6ff;padding:40px 36px;border-right:1px solid #e5e9f5;display:flex;flex-direction:column;justify-content:center}
  .reg-right{padding:40px 44px!important}
}
.reg-pitch{font-family:inherit;font-size:14px;color:#4b5566;line-height:1.5;margin:14px 0 20px}
`;

const styles = {
  page: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: colors.bgPage, padding: space[4], boxSizing: "border-box" },
  card: { width: "100%", maxWidth: 420, background: colors.surface, borderRadius: radius.lg, boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" },

  form: { display: "flex", flexDirection: "column", gap: space[3], padding: space[5] },
  heading: { margin: "0 0 4px", fontFamily: fonts.display, fontSize: 19, fontWeight: 600, textAlign: "center", color: colors.textPrimary },
  input: inputBase,
  button: buttonPrimary,
  error: { color: "#b91c1c", fontSize: 13, textAlign: "center", fontFamily: fonts.body },
  includedBox: { background: colors.surfaceMuted, margin: `0 ${space[5]}`, marginTop: space[4], padding: space[3], borderRadius: radius.sm },
  includedTitle: { fontFamily: fonts.display, fontSize: 13, fontWeight: 600, color: colors.textPrimary, marginBottom: 6 },
  includedList: { margin: 0, paddingLeft: 18, fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary, lineHeight: 1.6 },
  requestBox: { border: `1px dashed ${colors.border}`, borderRadius: radius.sm, padding: space[3], marginTop: space[2] },
  requestTitle: { fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary, marginBottom: space[2], lineHeight: 1.5 },
  requestRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" },
  requestLabel: { fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary },
  requestDefault: { color: colors.textMuted, fontSize: 11 },
  requestInput: { ...inputBase, width: 70, padding: "6px 8px" },
  checkboxRow: { display: "flex", alignItems: "center", gap: 8, fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary, marginTop: space[2] },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  termsModal: { width: 460, maxWidth: "calc(100vw - 24px)", boxSizing: "border-box", maxHeight: "85vh", overflowY: "auto", background: colors.surface, borderRadius: radius.lg, padding: space[5] },
  termsBody: { fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary, lineHeight: 1.6, maxHeight: 280, overflowY: "auto", border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: space[3] },
  termsCheckboxRow: { display: "flex", alignItems: "center", gap: 8, fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary, marginTop: space[3] },
};
